import React, { useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import ErrorPage from 'next/error';
import Episode from '../../endpoints/Episode/Episode';
import ContentGrid from '../../grids/ContentGrid';
import Sidebar from '../../components/Sidebar/Sidebar';
import fetcher from '../../utils/fetcher';
import Query from '../../endpoints/Episode/episode.gql';
import { parseEmbeddedAssets } from '../../utils/utils';
import { getServerSession } from 'next-auth';
import { authOptions } from 'pages/api/auth/[...nextauth]';
import AdCategoriesContext from 'context/AdCategoriesContext';
import getWordCount from '../../utils/getWordCount';
import setPageCacheHeaders from '../../utils/setPageCacheHeaders';

/* eslint react/display-name: 0 */

const EpisodePage = ({ data, errorCode, wordcount }) => {
  if (errorCode) return <ErrorPage statusCode={errorCode} />;
  const context = useContext(AdCategoriesContext);

  useEffect(() => {
    if (data.episode.primaryCollection)
      context.setPrimaryCategory(data.episode.primaryCollection.canonicalSlug);

    if (data.episode.collections)
      context.setAdCategories(
        data.episode.collections.map((col) => col.canonicalSlug)
      );
  }, []);

  let pageTopic;

  return (
    <ContentGrid
      className="story-content-grid"
      sidebar={<Sidebar pageTopic={pageTopic} />}
    >
      <div id="mainContent"></div>
      <Episode data={data} wordcount={wordcount} />
    </ContentGrid>
  );
};

export async function getServerSideProps({
  query: { slug, previewToken },
  req,
  res,
}) {
  let data,
    errorCode = false,
    layout = 'default';

  // Fetch episode data and session in parallel — see story/[...slug].js
  // for explanation of why this reduces TTFB.
  const [episodeResult, sessionResult] = await Promise.allSettled([
    fetcher(Query, {
      contentAreaSlug: process.env.CONTENT_AREA_SLUG,
      slug: slug.join('/'),
      previewToken: previewToken,
    }),
    getServerSession(req, res, authOptions),
  ]);

  if (episodeResult.status === 'fulfilled') {
    data = episodeResult.value;
    if (data?.episode?.embeddedAssets) {
      parseEmbeddedAssets(data.episode.embeddedAssets);
    }
    if (!data.episode) {
      if (res) res.statusCode = 404;
      errorCode = 404;
    } else {
      layout = data?.episode?.primaryCollection?.templateName;
    }
  } else {
    if (res) res.statusCode = 500;
    errorCode = 500;
  }

  const session = sessionResult.value ?? null;

  // Must run after the session resolves: caching is conditional on it.
  setPageCacheHeaders(res, session);

  const wordcount = getWordCount(data?.episode?.body);

  return {
    props: {
      data: data,
      errorCode: errorCode,
      layout,
      session,
      resourceType: data?.episode?.resourceType,
      wordcount,
    },
  };
}

EpisodePage.propTypes = {
  errorCode: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  data: PropTypes.object,
  wordcount: PropTypes.number,
};

export default EpisodePage;
